import React, {Component, useEffect, useState} from 'react';
import {Link} from "react-router-dom";
import {LayoutSidebar} from "../../../Layout";
import {AgencyHeader} from "../AgencyHeader";
import {AgencyTabs} from "../AgencyTabs";
import {APIClient} from "../../../utils/APIClient";
import {API_URL} from "../../../settings/local_settings";
import {AlertsConfiguration} from "../../common/AlertsConfiguration";
import {SaveBar} from "../../elements/SaveBar";
import {fetchCustomMetrics} from "../../../hooks/useCustomMetrics";
import {setCustomMetricLabelsCache} from "../../dashboard/columns";
import { Trans, withTranslation } from 'react-i18next';
import {UI_VERSION_V2, useUiVersion, useUserModules} from "../../../context/UserModulesContext";

/**
 * La única línea del Centro de Alertas en el clásico (tanda «Centro de Alertas», F3): quien volvió al
 * clásico no ve las reglas que se crearon en la 2.1, y esta pantalla tiene que decirle que existen.
 * Sólo pregunta con el Centro prendido en la agencia; si el pedido falla, no dice nada. El link abre
 * «Mis reglas» pasando a la 2.1 (el mismo gesto que el botón «2.1» del menú clásico): el Centro existe
 * sólo ahí, y un `?ui=v2` en la URL no cambia de marco en una navegación sin recarga.
 *
 * No importa nada de `alerts_center/` (guardián `classicIsolation.test.js`): pide la lista por su cuenta.
 */
export function AlertRulesNote() {
    const {alertRulesEnabled} = useUserModules();
    const {setUiVersion} = useUiVersion();
    const [count, setCount] = useState(0);

    useEffect(() => {
        if (alertRulesEnabled !== true) return undefined;
        let cancelled = false;
        new APIClient(`${API_URL}agency/alert-rules/`).call('GET').then((response) => {
            if (cancelled || !response || response.status !== 'OK' || !response.result) return;
            setCount(Array.isArray(response.result.rules) ? response.result.rules.length : 0);
        });
        return () => { cancelled = true; };
    }, [alertRulesEnabled]);

    if (alertRulesEnabled !== true || count === 0) return null;
    return (
        <div className="alert alert-info" data-testid="classic-alert-rules-note">
            <Trans i18nKey="alerts_center.classic_note" count={count}
                   components={[<Link key="rules" to="/alerts/rules"
                                      onClick={() => setUiVersion(UI_VERSION_V2)}/>]}/>
        </div>
    );
}

class AgencyAlertsConfigurationClass extends Component {
    saveBarRef = React.createRef();

    constructor(props) {
        super(props);
        this.state = {
            indicators: null,
            agencyFlexIndicators: null,
            agencyEcommerceFixedFlexIndicators: null,
            customMetricsReady: false,
            savingChanges: false,
            hasChanges: false
        };

        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleIndicatorChange = this.handleIndicatorChange.bind(this);
    }

    componentDidMount() {
        this.loadCustomMetricLabels();
        this.getInitialAlertConfiguration();
    }

    // Las métricas personalizadas (flex indicators tipo CUSTOM:N) se muestran con su
    // nombre legible resolviendo el cache de columns.js. Ese cache solo lo llena el
    // Dashboard, así que esta pantalla lo puebla por su cuenta para no mostrar "CUSTOM:6".
    loadCustomMetricLabels() {
        fetchCustomMetrics().then((items) => {
            setCustomMetricLabelsCache(items);
            this.setState({customMetricsReady: true});
        });
    }

    static configureAlertUrl() {
        return `${API_URL}agency/alerts-configuration`
    }

    // API

    getInitialAlertConfiguration() {
        new APIClient(AgencyAlertsConfigurationClass.configureAlertUrl()).call('GET').then((data) => {
            if (data.status === 'ERROR') {
                alert(data.error_message);
            } else if (data.status === 'OK') {
                const dataForForm = this.prepareDataForForm(data.result);
                this.setState({
                    indicators: dataForForm["configuration_per_indicator"],
                    agencyFlexIndicators: dataForForm["agency_flex_indicators"],
                    agencyEcommerceFixedFlexIndicators: dataForForm["agency_ecommerce_fixed_flex_indicators"]
                });
            }
        });
    }

    prepareDataForForm(data) {
        let indicators_result = {};
        Object.keys(data["configuration_per_indicator"]).forEach(indicatorName => {
            let indicator = data["configuration_per_indicator"][indicatorName];
            indicators_result[indicatorName] = {
                is_active: indicator.is_active,
                sensitivity_values: indicator.sensitivity_values.map((sensitivity_value) => {
                    return {
                        ...sensitivity_value,
                        value: (sensitivity_value.value !== null ? Math.round(sensitivity_value.value * 100) : null)
                    }
                })
            }
        });

        return {...data, "configuration_per_indicator":indicators_result};
    }

    prepareDataForSubmit() {
        let result = {};
        Object.keys(this.state.indicators).forEach(indicatorName => {
            const indicator = this.state.indicators[indicatorName];
            result[indicatorName] = {
                is_active: indicator.is_active,
                sensitivity_values: indicator.sensitivity_values.map((sensitivity_value) => {
                    return {
                        ...sensitivity_value,
                        value: (sensitivity_value.value !== null && sensitivity_value.value !== "" ? sensitivity_value.value / 100 : null)
                    }
                })
            }
        });
        return result;
    }

    closeSaveModal() {
        if (this.saveBarRef.current) {
            this.saveBarRef.current.closeSaveModal();
        }
    }

    // handlers

    handleSubmit() {
        const { t } = this.props;
        this.setState({savingChanges: true});

        const data = this.prepareDataForSubmit();
        new APIClient(AgencyAlertsConfigurationClass.configureAlertUrl()).call('POST', data).then((data) => {
            this.setState({savingChanges: false, hasChanges: false});
            if (data.status === 'ERROR') {
                alert(data.error_message);
            } else if (data.status === 'ERROR_USER_DOES_NOT_HAVE_ACCESS') {
                alert(data.error_message);
            } else if (data.status === 'OK') {
                alert(t('agency_alerts_configuration.changes_saved'));
                this.closeSaveModal();
            }
        });
    }

    handleIndicatorChange(indicators) {
        this.setState({
            indicators: indicators,
            hasChanges: true
        });
    }

    // rendering

    render() {
        const { t } = this.props;
        return (
            <div className="app-content">
                <LayoutSidebar/>
                <section className="section-content">
                    <AgencyHeader/>
                    <AgencyTabs activeTab="-fourth"/>
                    <div className="section-body h-items">
                        <div className="section-body-item -wider box">
                            <header className="box-header">
                                <h2 className="box-title">{t('agency_alerts_configuration.title')}</h2>
                            </header>
                            <AlertRulesNote/>
                            <div className="form-row">
                                <label>{t('agency_alerts_configuration.description')}</label>
                                <label>{t('agency_alerts_configuration.email_description')}</label>
                            </div>
                            <AlertsConfiguration
                                indicators={this.state.indicators}
                                agencyFlexIndicators={this.state.agencyFlexIndicators}
                                agencyEcommerceFixedFlexIndicators={this.state.agencyEcommerceFixedFlexIndicators}
                                onIndicatorsChange={this.handleIndicatorChange}
                            />
                        </div>
                        <SaveBar
                            ref={this.saveBarRef}
                            onClick={this.handleSubmit}
                            isSaving={this.state.savingChanges}
                            hasChanges={this.state.hasChanges}
                            userConfirmationNeeded={true}
                            title={t('agency_alerts_configuration.save_title')}
                            message={t('agency_alerts_configuration.save_message')}
                            question={t('agency_alerts_configuration.save_question')}
                        />
                    </div>
                </section>
            </div>
        )
    }
}

export const AgencyAlertsConfiguration = withTranslation()(AgencyAlertsConfigurationClass);
export default AgencyAlertsConfiguration;
